import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MessagePage from '../MessagePage';
import {
  PresentEntryDetail, PresentList, errorMessage, errorStatus, fetchPresentEntry, fetchPresentList,
} from '../../services/manageApi';
import SingleFileViewer from '../../components/viewers/SingleFileViewer';
import MultiPackageView from '../../components/viewers/MultiPackageView';
import {
  ProjectDescriptionSection, ProjectOverviewSection, ProjectSlidesSection, ProjectVideoSection, projectVideoLink,
} from '../../components/viewers/ProjectSubmission';
import { usesProjectLayout } from '../../components/viewers/submissionMedia';
import TourButton from '../../components/tour/TourButton';
import './PresentPage.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

type ProjectTab = 'video' | 'slides' | 'description' | 'details';

const PROJECT_TABS: { id: ProjectTab; label: string }[] = [
  { id: 'video', label: 'Video' },
  { id: 'slides', label: 'Slides' },
  { id: 'description', label: 'Description' },
  { id: 'details', label: 'Details' },
];

export function entryLabel(name: string, index: number, hideNames: boolean): string {
  return hideNames ? `Submission ${index + 1}` : name;
}

const SINGLE_FILE_TYPES = ['video', 'pdf', 'html', 'github', 'viz', 'image'];

/** Keys pressed while typing or while a media player has focus belong to that element, not to navigation. */
function isOwnedTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable) return true;
  if (el.tagName === 'VIDEO' || el.tagName === 'AUDIO') return true;
  return typeof el.closest === 'function' && el.closest('.evaluations-video-wrapper') !== null;
}

/**
 * /manage/:id/present — read-only view for live presentations.
 * Uses only /competitions/:id/present endpoints: no votes, scores or reservations.
 */
const PresentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const competitionId = Number(id);
  const hideNamesKey = `ikompete:present:hide-names:${competitionId}`;

  const [list, setList] = useState<PresentList | null>(null);
  useDocumentTitle(list?.competition?.title ? `Present · ${list.competition.title}` : undefined);
  const [listError, setListError] = useState<{ status?: number; message: string } | null>(null);
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState<PresentEntryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [projectTab, setProjectTab] = useState<ProjectTab>('video');
  // Entry whose detail last chose the project tab; a reload of the same entry keeps the tab.
  const tabEntryRef = useRef<string | null>(null);
  const [hideNames, setHideNames] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(hideNamesKey) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.sessionStorage.setItem(hideNamesKey, hideNames ? '1' : '0');
    } catch {
      // Storage unavailable: the toggle still works for this page view.
    }
  }, [hideNames, hideNamesKey]);

  useEffect(() => {
    let cancelled = false;
    fetchPresentList(competitionId)
      .then((result) => {
        if (cancelled) return;
        setList(result);
        const firstWithWork = result.entries.findIndex((entry) => entry.has_submission);
        setIndex(firstWithWork >= 0 ? firstWithWork : 0);
      })
      .catch((err) => {
        if (!cancelled) setListError({ status: errorStatus(err), message: errorMessage(err, "Couldn't load submissions") });
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  const entries = list?.entries ?? [];
  const current = entries[index];
  const currentId = current?.entry_id;

  useEffect(() => {
    if (!currentId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    // Clear first so the previous team's work never shows while the next loads.
    setDetail(null);
    setDetailLoading(true);
    setDetailError('');
    fetchPresentEntry(competitionId, currentId)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        if (tabEntryRef.current !== result.entry_id) {
          tabEntryRef.current = result.entry_id;
          setProjectTab(result.submission && projectVideoLink(result.submission) ? 'video' : 'slides');
        }
      })
      .catch((err) => {
        if (!cancelled) setDetailError(errorMessage(err, "Couldn't load this submission"));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId, currentId, reloadToken]);

  const go = useCallback((delta: number) => {
    setIndex((i) => (entries.length ? Math.min(Math.max(i + delta, 0), entries.length - 1) : i));
  }, [entries.length]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.metaKey || event.ctrlKey) return;
      if (isOwnedTarget(event.target)) return;
      if (event.key === 'ArrowRight') go(1);
      else if (event.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };

  const reload = () => setReloadToken((token) => token + 1);

  if (listError) {
    if (listError.status === 401 || listError.status === 403) {
      return (
        <MessagePage
          title="Access Denied"
          message="Only hosts of this competition can use Present mode."
          type="error"
          showCloseButton={true}
        />
      );
    }
    return <div className="present-page present-page--message" data-theme="dark"><p role="alert">{listError.message}</p></div>;
  }

  if (!list) {
    return <div className="present-page present-page--message" data-theme="dark"><LoadingSpinner text="Loading submissions..." /></div>;
  }

  const competition = list.competition;
  const type = (competition.type || '').toLowerCase();
  // The detail effect resets `detail` only after the render that changed the entry,
  // so ignore a detail that belongs to another entry instead of showing it for a frame.
  const shownDetail = detail && detail.entry_id === currentId ? detail : null;
  const showProjectTabs = type === 'multi' && usesProjectLayout(competition.id);
  const currentLabel = current ? entryLabel(current.name, index, hideNames) : '';
  const rawSubmission = shownDetail?.submission ?? null;
  // Viewers print the team name (e.g. the project Details tab), so hide it there too.
  const submission = rawSubmission && hideNames
    ? { ...rawSubmission, team_name: null, metadata_team: currentLabel }
    : rawSubmission;

  let stage: React.ReactNode;
  if (!entries.length) {
    stage = <p className="present-stage__empty">No participants yet.</p>;
  } else if (detailError) {
    stage = (
      <div className="present-stage__empty" role="alert">
        {detailError}{' '}
        <button type="button" className="present-bar__button" onClick={reload}>Retry</button>
      </div>
    );
  } else if (detailLoading || !shownDetail) {
    stage = <LoadingSpinner text="Loading submission..." />;
  } else if (!submission) {
    stage = <p className="present-stage__empty">No submission yet.</p>;
  } else if (showProjectTabs) {
    stage = (
      <>
        <div className="present-tabs" role="tablist" aria-label="Submission parts">
          {PROJECT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={projectTab === tab.id}
              className={`present-tabs__tab${projectTab === tab.id ? ' present-tabs__tab--active' : ''}`}
              onClick={() => setProjectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="present-project evaluations-multi-card" role="tabpanel" data-theme="light">
          {projectTab === 'video' && (
            <ProjectVideoSection
              key={`${submission.submission_id}-${submission.submission_version}`}
              submission={submission}
              competitionId={competition.id}
            />
          )}
          {projectTab === 'slides' && <ProjectSlidesSection submission={submission} competitionId={competition.id} />}
          {projectTab === 'description' && <ProjectDescriptionSection submission={submission} competitionId={competition.id} />}
          {projectTab === 'details' && <ProjectOverviewSection submission={submission} competitionId={competition.id} />}
        </div>
      </>
    );
  } else if (type === 'multi') {
    // The shared viewers render fixed-white cards, so their tokens resolve light inside the dark stage.
    stage = <div data-theme="light"><MultiPackageView submission={submission} /></div>;
  } else if (!SINGLE_FILE_TYPES.includes(type)) {
    stage = (
      <div className="evaluations-image-card" data-theme="light">
        <div className="evaluations-image-label">{currentLabel}</div>
        <a href={submission.url} target="_blank" rel="noopener noreferrer" className="evaluations-pdf-link">
          Open file
        </a>
      </div>
    );
  } else {
    stage = (
      <div data-theme="light">
        <SingleFileViewer
          type={type}
          url={submission.url}
          label={currentLabel}
          onImageClick={(url) => window.open(url, '_blank', 'noopener')}
        />
      </div>
    );
  }

  return (
    <div className="present-page" data-theme="dark">
      <header className="present-bar">
        <strong className="present-bar__title">{competition.title}</strong>
        <span className="present-bar__badge">PRESENT · no voting</span>
        <select
          className="present-bar__select"
          data-tour="present-select"
          aria-label="Submission to show"
          value={index}
          onChange={(e) => setIndex(Number(e.target.value))}
          disabled={!entries.length}
        >
          {entries.map((entry, i) => (
            <option key={entry.entry_id} value={i}>
              {entryLabel(entry.name, i, hideNames)}{entry.has_submission ? '' : ' (no submission)'}
            </option>
          ))}
        </select>
        <button type="button" className="present-bar__button" data-tour="present-nav" onClick={() => go(-1)} disabled={index <= 0}>◀ Prev</button>
        <button type="button" className="present-bar__button" data-tour="present-nav" onClick={() => go(1)} disabled={index >= entries.length - 1}>Next ▶</button>
        <span className="present-bar__spacer" />
        <button type="button" className="present-bar__button" data-tour="present-reload" onClick={reload} disabled={!current}>↻ Reload</button>
        <button
          type="button"
          className="present-bar__button"
          aria-pressed={hideNames}
          data-tour="present-names"
          title="Hides names in this bar and the heading. Names inside a submission's own files stay visible."
          onClick={() => setHideNames((value) => !value)}
        >
          {hideNames ? 'Show names' : 'Hide names'}
        </button>
        <button type="button" className="present-bar__button" data-tour="present-fullscreen" onClick={toggleFullscreen}>⛶ Full screen</button>
        <TourButton variant="bar" className="present-bar__button" />
      </header>

      <main className="present-stage" data-tour="present-stage">
        {current && submission && !detailError && (
          <div className="present-stage__meta">
            <h1>{currentLabel}</h1>
            {!hideNames && current.members.length > 0 && <p>{current.members.join(', ')}</p>}
          </div>
        )}
        {stage}
      </main>
    </div>
  );
};

export default PresentPage;
